// @ts-check

/** @file Renders one consistently attributed comment in a task conversation. */

import _ from "gettext-universal/build/src/translate.js"
import {View} from "react-native-propforge"
import Text from "@kaspernj/api-maker/build/utils/text"
import Card from "../card"
import RenderHtml from "../render-html"
import useThemeColors, {useThemeName} from "../../shared/use-theme-colors"

/** @import {ReactNode} from "react" */
/** @typedef {{automationKind: () => string | null, comment: () => string | null, createdAt: () => Date | string | null, user: () => {firstName: () => string | null, lastName: () => string | null} | null}} CommentReadRecord */
/** @type {Record<string, object>} */
const styles = {}

/**
 * Renders one human comment, TensorBuzz reply, or TensorBuzz command status.
 * @param {{comment: CommentReadRecord, compact?: boolean, testID: string, unknownAuthorLabel?: string}} props Component props.
 * @returns {ReactNode} Rendered thread entry.
 */
export default function TaskThreadComment({comment, compact = false, testID, unknownAuthorLabel}) {
  const colors = useThemeColors()
  const themeName = useThemeName()
  const automationKind = comment.automationKind()
  const automated = automationKind === "reply" || automationKind === "command_feedback"
  const commandFeedback = automationKind === "command_feedback"
  const user = comment.user()
  const author = automationKind === "reply"
    ? _("TensorBuzz bot")
    : commandFeedback
      ? _("TensorBuzz command")
      : user
        ? `${user.firstName()} ${user.lastName()}`
        : unknownAuthorLabel
  const createdAt = comment.createdAt()

  return (
    <Card
      style={styles[`card-${themeName}-${compact}-${commandFeedback}`] ||= {
        backgroundColor: commandFeedback ? colors.surfaceMuted : colors.surface,
        marginBottom: 12,
        ...(compact ? {padding: 12} : {})
      }}
      testID={testID}
    >
      <View
        style={styles[`header-${compact}`] ||= {
          alignItems: "center",
          flexDirection: compact ? "column" : "row",
          marginBottom: 8
        }}
        testID={`${testID}/header`}
      >
        {author &&
          <Text
            style={styles[`author-${themeName}-${automated}-${commandFeedback}`] ||= {
              alignSelf: compact ? "flex-start" : undefined,
              color: commandFeedback ? colors.textMuted : automated ? colors.text : colors.link,
              fontSize: 14,
              fontWeight: 600
            }}
            testID={`${testID}/author`}
          >
            {author}
          </Text>
        }
        <Text
          style={styles[`timestamp-${themeName}-${compact}`] ||= {
            alignSelf: compact ? "flex-start" : undefined,
            color: colors.textMuted,
            fontSize: 13,
            marginLeft: compact ? 0 : "auto",
            marginTop: compact ? 2 : 0
          }}
          testID={`${testID}/timestamp`}
        >
          {createdAt ? new Date(createdAt).toLocaleString() : ""}
        </Text>
      </View>
      <View style={styles.body ||= {marginTop: 0}} testID={`${testID}/body`}>
        <RenderHtml source={comment.comment() || ""} />
      </View>
    </Card>
  )
}
